import React, { memo, useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";

// Colors
const COLORS = {
  root: "#616161",
  positive: "#2e7d32",
  mgBNegative: "#b71c1c",
  mgSNegative: "#d32f2f",
  ebitdaNegative: "#f57c00",
  naBackground: "#cccccc",
};

const useStyles = makeStyles({
  container: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden", // Hide scrollbars
  },
  wrapper: {
    position: "relative",
    width: "100%",
    flex: 1,
    minHeight: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden", // Hide scrollbars
  },
  canvas: {
    position: "absolute",
    top: 0,
    left: 0,
    pointerEvents: "none",
    width: "100%",
    height: "100%",
  },
  nodesContainer: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "3%",
    padding: "0.5rem 0",
    transformOrigin: "center center",
    // Base width - will be scaled to fit
    width: "100%",
  },
  column: {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 0",
    minWidth: 0,
    gap: "0.75rem",
  },
  node: {
    minHeight: "4.2rem",
    borderRadius: "0.375rem",
    padding: "0.25rem 0.4rem",
    textAlign: "center",
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    boxSizing: "border-box",
    transition: "filter 0.15s ease, transform 0.15s ease",
  },
  nodeClickable: {
    cursor: "pointer",
    "&:hover": {
      filter: "brightness(1.15)",
      transform: "scale(1.02)",
    },
  },
  nodeTitle: {
    fontSize: "0.8125rem", // 13px
    fontWeight: 400,
    marginBottom: "0.25rem",
    lineHeight: 1.2,
  },
  nodeSubtitle: {
    fontSize: "0.75rem", // 12px
    fontWeight: 300,
    opacity: 0.95,
    lineHeight: 1.4,
  },
  nodeLine: {
    display: "flex",
    justifyContent: "center",
    gap: "0.25rem",
  },
  nodePercent: {
    opacity: 0.7,
  },
  nodeNaText: {
    fontSize: "0.75rem", // 12px
    fontWeight: 400,
    fontStyle: "italic",
    opacity: 0.8,
    lineHeight: 1.4,
  },
});

// Format number with magnitude (with space before unit)
const formatValue = (value) => {
  if (value === null || value === undefined) return "-";

  const absValue = Math.abs(value);
  let formatted;

  if (absValue >= 1e9) {
    formatted = (value / 1e9).toFixed(1) + " B";
  } else if (absValue >= 1e6) {
    formatted = (value / 1e6).toFixed(1) + " M";
  } else if (absValue >= 1e3) {
    formatted = (value / 1e3).toFixed(0) + " K";
  } else {
    formatted = value.toFixed(0);
  }

  return formatted;
};

// Format delta with sign
const formatDeltaValue = (curr, prev) => {
  if (prev === undefined || prev === null) return null;
  const diff = curr - prev;
  if (diff === 0) return null;
  const sign = diff >= 0 ? "+" : "";
  return `${sign}${formatValue(diff)}`;
};

const formatDeltaCount = (curr, prev) => {
  if (prev === undefined || prev === null) return null;
  const diff = curr - prev;
  if (diff === 0) return null;
  const sign = diff >= 0 ? "+" : "";
  return `${sign}${diff.toLocaleString("pt-BR")}`;
};

// Node component
function TreeNode({
  data,
  color,
  isNa = false,
  totals: _totals,
  elementLabel = "clientes",
  onClick,
}) {
  void _totals;
  const classes = useStyles();

  const hasPrev = data.clients_prev !== undefined;

  // Raw deltas for color coding
  const dClients = hasPrev ? data.clients - data.clients_prev : null;
  const dRevenue = hasPrev ? data.revenue - data.revenue_prev : null;
  const dMargin = hasPrev ? data.margin - data.margin_prev : null;

  const deltaClients = hasPrev
    ? formatDeltaCount(data.clients, data.clients_prev)
    : null;
  const deltaRevenue = hasPrev
    ? formatDeltaValue(data.revenue, data.revenue_prev)
    : null;
  const deltaMargin = hasPrev
    ? formatDeltaValue(data.margin, data.margin_prev)
    : null;

  // Delta badge: gray background, colored text
  const deltaBadge = (text, diff) => {
    if (!text) return null;
    const clr =
      diff > 0 ? "#a5d6a7" : diff < 0 ? "#ef9a9a" : "rgba(255,255,255,0.6)";
    return (
      <span
        style={{
          backgroundColor: "rgba(0,0,0,0.3)",
          color: clr,
          borderRadius: "3px",
          padding: "1px 5px",
          fontSize: "0.65rem",
          fontWeight: 500,
          whiteSpace: "nowrap",
        }}
      >
        {text}
      </span>
    );
  };

  // Row: label on left, value + delta on right
  const metricRow = (label, value, deltaText, deltaRaw) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "1px 0",
      }}
    >
      <span style={{ opacity: 0.8, fontSize: "0.65rem" }}>{label}</span>
      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <span style={{ fontSize: "0.75rem", fontWeight: 400 }}>{value}</span>
        {deltaBadge(deltaText, deltaRaw)}
      </span>
    </div>
  );

  const isClickable = !isNa && onClick;

  return (
    <div
      className={`${classes.node} ${isClickable ? classes.nodeClickable : ""}`}
      style={{ backgroundColor: isNa ? COLORS.naBackground : color }}
      data-node-id={data.id}
      onClick={isClickable ? () => onClick(data.id) : undefined}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      <div className={classes.nodeTitle}>{data.title}</div>
      {isNa ? (
        <div className={classes.nodeSubtitle}>
          <div className={classes.nodeLine}>
            <span className={classes.nodeNaText}>n/a</span>
          </div>
        </div>
      ) : (
        <div className={classes.nodeSubtitle} style={{ padding: "2px 4px 0" }}>
          {metricRow(
            elementLabel.charAt(0).toUpperCase() + elementLabel.slice(1),
            data.clients.toLocaleString("pt-BR"),
            deltaClients,
            dClients,
          )}
          {metricRow(
            "Faturamento",
            formatValue(data.revenue),
            deltaRevenue,
            dRevenue,
          )}
          {metricRow(
            "Margem de Servir",
            `${data.margin < 0 ? "-" : ""}${formatValue(Math.abs(data.margin))}`,
            deltaMargin,
            dMargin,
          )}
        </div>
      )}
    </div>
  );
}

TreeNode.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string.isRequired,
    clients: PropTypes.number,
    revenue: PropTypes.number,
    margin: PropTypes.number,
    clients_prev: PropTypes.number,
    revenue_prev: PropTypes.number,
    margin_prev: PropTypes.number,
  }).isRequired,
  color: PropTypes.string.isRequired,
  isNa: PropTypes.bool,
  totals: PropTypes.shape({
    clients: PropTypes.number,
    revenue: PropTypes.number,
    margin: PropTypes.number,
  }),
  elementLabel: PropTypes.string,
  onClick: PropTypes.func,
};

/**
 * ClientCascadeTree - Shows client/product distribution across P&L levels
 * Structure:
 * Level 1: Total Company
 * Level 2: Gross Margin + / Gross Margin -
 * Level 3: Serve Margin + / Serve Margin - (from Gross Margin +)
 * Level 4: EBITDA + / EBITDA - (from Serve Margin +, optional)
 */
// Map internal node IDs (used for canvas connectors) to backend cascade_node values
const NODE_ID_TO_CASCADE = {
  root: "root",
  "mgb-pos": "gm_positive",
  "mgb-neg": "gm_negative",
  "mgs-pos": "sm_positive",
  "mgs-neg": "sm_negative",
};

function ClientCascadeTree({
  data,
  showEbitda = true,
  elementLabel = "clientes",
  onNodeClick,
}) {
  const classes = useStyles();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const nodesRef = useRef(null);
  const [scale, setScale] = useState(1);

  // Check if EBITDA data is available
  const hasEbitda = showEbitda && data?.ebitda;

  // Calculate scale to fit content in container
  useEffect(() => {
    const calculateScale = () => {
      if (!containerRef.current || !nodesRef.current) return;

      const container = containerRef.current;
      const nodes = nodesRef.current;

      // Reset scale to measure natural size
      nodes.style.transform = "scale(1)";

      const containerRect = container.getBoundingClientRect();
      const nodesRect = nodes.getBoundingClientRect();

      // Calculate scale factors for width and height
      const scaleX = containerRect.width / nodesRect.width;
      const scaleY = containerRect.height / nodesRect.height;

      // Use the smaller scale to ensure content fits in both dimensions
      // Cap at 1 to avoid scaling up, and set minimum to avoid too small
      const newScale = Math.min(Math.max(Math.min(scaleX, scaleY), 0.5), 1);

      setScale(newScale);
    };

    // Initial calculation with delay
    const timeoutId = setTimeout(calculateScale, 100);

    // Recalculate on resize
    let resizeObserver = null;
    if (containerRef.current) {
      resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(calculateScale);
      });
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [data, showEbitda]);

  // Draw connectors on canvas
  useEffect(() => {
    const drawConnections = () => {
      if (!canvasRef.current || !containerRef.current || !data) return;

      const canvas = canvasRef.current;
      const container = containerRef.current;
      const ctx = canvas.getContext("2d");

      // Set canvas size to match container (account for device pixel ratio)
      const rect = container.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      ctx.scale(dpr, dpr);

      // Clear canvas
      ctx.clearRect(0, 0, rect.width, rect.height);

      // Line style
      ctx.strokeStyle = "#555";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";

      // Get node positions
      const getNodeCenter = (nodeId, side = "right") => {
        const node = container.querySelector(`[data-node-id="${nodeId}"]`);
        if (!node) return null;

        const nodeRect = node.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        return {
          x:
            side === "right"
              ? nodeRect.right - containerRect.left
              : nodeRect.left - containerRect.left,
          y: nodeRect.top - containerRect.top + nodeRect.height / 2,
        };
      };

      // Draw connection between two nodes
      const drawConnection = (fromId, toId) => {
        const from = getNodeCenter(fromId, "right");
        const to = getNodeCenter(toId, "left");

        if (!from || !to) return;

        const midX = from.x + (to.x - from.x) / 2;

        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(midX, from.y);
        ctx.lineTo(midX, to.y);
        ctx.lineTo(to.x, to.y);
        ctx.stroke();
      };

      // Draw all connections
      // Level 1 -> Level 2
      drawConnection("root", "mgb-pos");
      drawConnection("root", "mgb-neg");

      // Level 2 -> Level 3
      drawConnection("mgb-pos", "mgs-pos");
      drawConnection("mgb-pos", "mgs-neg");

      // Level 3 -> Level 4 (if EBITDA is shown)
      if (showEbitda) {
        drawConnection("mgs-pos", "ebitda-pos");
        drawConnection("mgs-pos", "ebitda-neg");
      }
    };

    // Debounced draw to avoid excessive redraws
    let rafId = null;
    const debouncedDraw = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        // Double RAF to ensure layout is complete
        rafId = requestAnimationFrame(drawConnections);
      });
    };

    // Initial draw with delay to ensure DOM is ready
    debouncedDraw();

    // Use ResizeObserver for more reliable resize detection
    let resizeObserver = null;
    if (containerRef.current) {
      resizeObserver = new ResizeObserver(() => {
        debouncedDraw();
      });
      resizeObserver.observe(containerRef.current);

      // Also observe all node elements for size changes
      const nodes = containerRef.current.querySelectorAll("[data-node-id]");
      nodes.forEach((node) => resizeObserver.observe(node));
    }

    // Fallback: window resize
    window.addEventListener("resize", debouncedDraw);

    // Redraw after fonts load (can affect node sizes)
    document.fonts?.ready?.then(debouncedDraw);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", debouncedDraw);
    };
  }, [data, showEbitda, scale]);

  // Handler that translates internal node IDs to backend cascade_node keys
  const handleNodeClick = onNodeClick
    ? (nodeId) => {
        const cascadeKey = NODE_ID_TO_CASCADE[nodeId];
        if (cascadeKey) onNodeClick(cascadeKey);
      }
    : undefined;

  if (!data) return null;

  return (
    <div className={classes.container}>
      <div className={classes.wrapper} ref={containerRef}>
        <canvas ref={canvasRef} className={classes.canvas} />

        <div
          className={classes.nodesContainer}
          ref={nodesRef}
          style={{ transform: `scale(${scale})` }}
        >
          {/* Level 1: Root (Total Company) - centered vertically */}
          <div
            className={classes.column}
            style={{ justifyContent: "center", display: "flex" }}
          >
            <TreeNode
              data={{ ...data.root, id: "root" }}
              color={COLORS.root}
              elementLabel={elementLabel}
              onClick={handleNodeClick}
            />
          </div>

          {/* Level 2: Gross Margin + / - */}
          <div className={classes.column}>
            <TreeNode
              data={{ ...data.grossMargin.positive, id: "mgb-pos" }}
              color={COLORS.positive}
              totals={data.root}
              elementLabel={elementLabel}
              onClick={handleNodeClick}
            />
            <TreeNode
              data={{ ...data.grossMargin.negative, id: "mgb-neg" }}
              color={COLORS.mgBNegative}
              totals={data.root}
              elementLabel={elementLabel}
              onClick={handleNodeClick}
            />
          </div>

          {/* Level 3: Serve Margin + / - (from Gross Margin +) */}
          <div className={classes.column}>
            <TreeNode
              data={{ ...data.serveMargin.positive, id: "mgs-pos" }}
              color={COLORS.positive}
              totals={data.root}
              elementLabel={elementLabel}
              onClick={handleNodeClick}
            />
            <TreeNode
              data={{ ...data.serveMargin.negative, id: "mgs-neg" }}
              color={COLORS.mgSNegative}
              totals={data.root}
              elementLabel={elementLabel}
              onClick={handleNodeClick}
            />
          </div>

          {/* Level 4: EBITDA + / - (from Serve Margin +, or n/a) */}
          {showEbitda && (
            <div className={classes.column}>
              {hasEbitda ? (
                <>
                  <TreeNode
                    data={{ ...data.ebitda.positive, id: "ebitda-pos" }}
                    color={COLORS.positive}
                    totals={data.root}
                    elementLabel={elementLabel}
                  />
                  <TreeNode
                    data={{ ...data.ebitda.negative, id: "ebitda-neg" }}
                    color={COLORS.ebitdaNegative}
                    totals={data.root}
                    elementLabel={elementLabel}
                  />
                </>
              ) : (
                <>
                  <TreeNode
                    data={{ id: "ebitda-pos", title: "EBITDA positivo" }}
                    color={COLORS.naBackground}
                    isNa={true}
                  />
                  <TreeNode
                    data={{ id: "ebitda-neg", title: "EBITDA negativo" }}
                    color={COLORS.naBackground}
                    isNa={true}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

ClientCascadeTree.propTypes = {
  data: PropTypes.shape({
    root: PropTypes.object.isRequired,
    grossMargin: PropTypes.shape({
      positive: PropTypes.object.isRequired,
      negative: PropTypes.object.isRequired,
    }).isRequired,
    serveMargin: PropTypes.shape({
      positive: PropTypes.object.isRequired,
      negative: PropTypes.object.isRequired,
    }).isRequired,
    ebitda: PropTypes.shape({
      positive: PropTypes.object,
      negative: PropTypes.object,
    }),
  }),
  showEbitda: PropTypes.bool,
  elementLabel: PropTypes.string,
  onNodeClick: PropTypes.func,
};

export default memo(ClientCascadeTree);
